import React from 'react';
import {
    ActiveMilitaryPersonalInfoViewModel,
    DatabaseId,
    MemberPersonalInfoViewModel,
    Segment,
    SegmentEnum,
    setViewModel,
    VerificationForm,
    ViewModel,
} from '@sheerid/jslib';

import { FormattedHTMLMessage } from 'react-intl';
import { GoogleLoginResponse } from 'react-google-login';
import { stateDepartmentProgramId, militaryProgramId } from '../programOptions';
import { FormFooterComponent } from './FormFooterComponent';
import { GoogleLoginWrapperComponent } from './FormFields/GoogleLoginWrapper';
import { getQueryParamsByName } from '../lib/utils/browser';
import {
    setGoogleOauthEmail,
    setGoogleOauthFirstName,
    setGoogleOauthId,
    setGoogleOauthLastName,
    getGoogleOauthFirstName,
    getGoogleOauthLastName,
    getGoogleOauthEmail,
    getGoogleOauthId,
} from '../lib/oauth/oauth';


const LandingPageComponent = () => {
    // Note that the actual programId when a verificationId is in the query string doesn't matter.
    //  The jslib will render the correct program once it fetches the verification status of the verificationId
    const hasVerificationId = getQueryParamsByName('verificationId') ? true : false;
    const [programId, setProgramId] = React.useState<DatabaseId>(hasVerificationId ? militaryProgramId : undefined);

    const handleLoginOnSuccess = (segment: Segment, response: GoogleLoginResponse) => {
        let viewModel: ViewModel;
        setGoogleOauthEmail((response as GoogleLoginResponse).getBasicProfile().getEmail());
        setGoogleOauthId((response as GoogleLoginResponse).getBasicProfile().getId());
        setGoogleOauthFirstName((response as GoogleLoginResponse).getBasicProfile().getGivenName());
        setGoogleOauthLastName((response as GoogleLoginResponse).getBasicProfile().getFamilyName());

        if (segment === SegmentEnum.MILITARY) {
            viewModel = {
                firstName: getGoogleOauthFirstName(),
                lastName: getGoogleOauthLastName(),
                email: getGoogleOauthEmail(),
                status: undefined,
                birthDate: '',
                countryChoice: {
                    label: '',
                    value: 'US',
                },
                localeChoice: {
                    label: '',
                    value: 'en-US',
                },
                organization: {
                    name: '',
                    id: 0,
                },
                metadata: {
                    googleEmail: getGoogleOauthEmail(),
                    googleId: getGoogleOauthId(),
                },
            } as ActiveMilitaryPersonalInfoViewModel;

        } else if (segment === SegmentEnum.MEMBER) {
            viewModel = {
                firstName: getGoogleOauthFirstName(),
                lastName: getGoogleOauthLastName(),
                email: '',
                memberId: '',
                countryChoice: {
                    label: '',
                    value: 'US',
                },
                localeChoice: {
                    label: '',
                    value: 'en-US',
                },
                organization: { id: 5150082, name: 'State Department' },
                metadata: {
                    googleEmail: getGoogleOauthEmail(),
                    googleId: getGoogleOauthId(),
                },
            } as MemberPersonalInfoViewModel;
        }

        setViewModel(viewModel);
        setProgramId(segment === SegmentEnum.MILITARY ? militaryProgramId : stateDepartmentProgramId);
    };

    return (
        <div className="sid-landing-page">
            <div className="sid-headband">
                <div className="sid-headband__logo">
                    <img src="./img/google-fi-logo.png" alt="Google Fi Logo" />
                </div>
            </div>
            {
                programId ?
                    <div className="sid-verification-form">
                        <VerificationForm programId={programId} />
                    </div>
                :
                <div className="sid-l-container">
                    <div className="sid-header">
                        <div className="sid-header__title">
                            <FormattedHTMLMessage
                                id="landing.title"
                                defaultMessage="Verify your government status"
                            />
                        </div>
                        <div className="sid-header__subtitle">
                            <FormattedHTMLMessage
                                id="landing.subtitle"
                                defaultMessage="Please verify your status as active duty/reservist military or State Department employee currently serving abroad, and Google Fi will process your international exception request."
                            />
                        </div>
                        <div className="sid-header__subtitle sid-l-space-top-md">
                            <FormattedHTMLMessage
                                id="landing.pleaseNote"
                                defaultMessage="Please note that your Fi account must either be currently <a class='sid-link' href='https://support.google.com/fi/answer/6078618' target='_blank' rel='noreferrer'>active</a> when applying for an international roaming exception or <a class='sid-link' href='https://support.google.com/fi/answer/6079346' target='_blank' rel='noreferrer'>paused</a> for an extended pausing exception."
                            />
                        </div>
                        <div className="sid-header__subtitle sid-l-space-top-md">
                            <FormattedHTMLMessage
                                id="landing.otherMembers"
                                defaultMessage="If you have dependents, add them to your group plan before proceeding. Once your status is verified, your group plan members will receive the same exception. Dependents should not submit a separate request."
                            />
                        </div>
                    </div>

                    <div className="sid-landing__program-btn sid-l-space-top-md">
                        <GoogleLoginWrapperComponent
                            onSuccess={response => handleLoginOnSuccess(SegmentEnum.MILITARY, response as GoogleLoginResponse)}
                            buttonComponent={renderProps => (
                                <button
                                    className="sid-btn sid-btn--dark"
                                    onClick={() => {
                                        renderProps.onClick();
                                    }}
                                >
                                    <FormattedHTMLMessage
                                        id="landing.verifyMilitary"
                                        defaultMessage="Verify your military status"
                                    />
                                </button>
                            )}
                        />

                        <div className="sid-l-space-top-md"></div>

                        <GoogleLoginWrapperComponent
                            onSuccess={response => handleLoginOnSuccess(SegmentEnum.MEMBER, response as GoogleLoginResponse)}
                            buttonComponent={renderProps => (
                                <button
                                    className="sid-btn sid-btn--dark"
                                    onClick={() => {
                                        renderProps.onClick();
                                    }}
                                >
                                    <FormattedHTMLMessage
                                        id="landing.verifyStateDepartment"
                                        defaultMessage="Verify your state department employment"
                                    />
                                </button>
                            )}
                        />
                    </div>

                    <hr className="sid-l-horz-separator sid-l-space-top-md"></hr>

                    <FormFooterComponent />

                </div>
            }
        </div>
    );
};

export default LandingPageComponent;
